const MILLISECONDS_PER_EPOCH = 1000;

const leadingZero = (int) => (int < 10 ? `0${int}` : `${int}`);

/**
 * Normalizes a `YYYY-MM-DD` string or `Date` to a `YYYY-MM-DD` string.
 * @param {string|Date} date
 * @returns {string|null} `null` if date is neither a string nor a valid Date.
 */
const dateToIsoString = (date) => {
    if (date instanceof Date) {
        if (Number.isNaN(date.getTime())) return null;

        const year = date.getFullYear();
        const month = leadingZero(date.getMonth() + 1);
        const day = leadingZero(date.getDate());
        return `${year}-${month}-${day}`;
    }
    return typeof date === 'string' ? date : null;
};

/**
 * Helper to convert a date and time string into a UNIX Epoch.
 * The date/time are treated as local time; the resulting epoch is UTC.
 * @param {string|Date} date - A `YYYY-MM-DD` string, or a `Date` whose local calendar date is used.
 * @param {string} timeString
 * @returns {number} The number value in UNIX epoch. NaN if date/timeString together are invalid.
 */
export const toEpoch = (date, timeString) => {
    const timeNotAString = typeof timeString !== 'string';
    if (timeNotAString) return NaN;

    const dateString = dateToIsoString(date);
    if (dateString === null) return NaN;

    const timestamp = new Date(`${dateString}T${timeString}`);

    if (Number.isNaN(timestamp.getTime())) return NaN;

    // getTime() returns UNIX epoch but with milliseconds epoch included, jsut round up.
    const toUnixEpoch = Math.floor(timestamp.getTime() / MILLISECONDS_PER_EPOCH);
    return toUnixEpoch;
};

/**
 * Converts UNIX epochs to the 24h time, converted to local timezone.
 * @param {number} epochSeconds
 * @returns {string} E.g. '21:00' for epoch at 9PM
 */
export const epochToTime = (epochSeconds) => {
    if (typeof epochSeconds !== 'number') return '';

    const date = new Date(epochSeconds * MILLISECONDS_PER_EPOCH);
    if (Number.isNaN(date.getTime())) return '';

    return `${leadingZero(date.getHours())}:${leadingZero(date.getMinutes())}`;
};

/**
 * Converts a UNIX epoch to a `YYYY-MM-DD` date string, converted to local timezone
 * (matching `epochToTime`'s local treatment, so the pair round-trips through `toEpoch`).
 * @param {number} epochSeconds
 * @returns {string} E.g. '2026-09-10'. Empty string if epochSeconds is invalid.
 */
export const epochToDate = (epochSeconds) => {
    if (typeof epochSeconds !== 'number') return '';

    const date = new Date(epochSeconds * MILLISECONDS_PER_EPOCH);
    if (Number.isNaN(date.getTime())) return '';

    return `${date.getFullYear()}-${leadingZero(date.getMonth() + 1)}-${leadingZero(date.getDate())}`;
};
